import { useEffect } from "react";
import { CancelablePromise } from "cancelable-promise";
import { JamCanvasFeatureProps } from "../types.js";
import { MapApi } from "../api/map.js";
import { AtlasApi } from "../api/atlas.js";
import { cancelApiRequest, useApi } from "../hooks/api.js";
import { useDrawerParams } from "../hooks/params.js";
import { useDrawer } from "../hooks/drawer.js";
import { useMonitor } from "../hooks/monitor.js";
import { CanvasDrawer } from "../utils/drawer.js";
import { fromImageBitmap, getCanvasContext } from "../utils/canvas.js";
import { BACKGROUND_COLOR } from "../const.js";
import { isCancelablePromise } from "../utils/cancelable-fetch.js";

export function JamCanvasFeature({
  featureId,
  ...args
}: Partial<JamCanvasFeatureProps>) {
  const disabled = !featureId;
  const options = useDrawer({ ...args, disabled });
  if (disabled || !options) return null;
  return <JamCanvasFeatureRenderer {...options} featureId={featureId} />;
}

export function JamCanvasFeatureRenderer({
  tilesProps,
  canvasDrawer,
  canvasStatus,
  drawersObservable,
  controls,
  drawerIndex,
  featureId,
  onError,
  showMonitor = false,
  options = {},
  ...params
}: JamCanvasFeatureProps) {
  useMonitor({
    showMonitor,
    id: "monitor-feature",
    canvasDrawer,
    bottom: "0",
    left: "0",
  });

  const { margin } = tilesProps;
  const { bgColor = BACKGROUND_COLOR, body, ...props } = options;
  body && delete body.search;
  const search = {
    q: [featureId],
    qa: ["id"],
    limit: 1,
    f: ["id"],
  };

  const { mapParams, atlasParams } = useDrawerParams({
    ...params,
    ...props,
    body,
    search,
    margin,
    bgColor,
  });
  const featureApi = useApi<Blob>();

  useEffect(() => {
    if (!mapParams) return;
    const { body } = mapParams;
    const req = MapApi.Image(mapParams, body);
    featureApi.setDataRequest(req);
  }, [mapParams, canvasDrawer]);

  useEffect(() => {
    if (!atlasParams) return;
    const { body } = atlasParams;
    const req = AtlasApi.Image(atlasParams, body, "high");
    featureApi.setDataRequest(req);
  }, [atlasParams, canvasDrawer]);

  useEffect(() => {
    featureApi.dataRequest
      ?.catch((e) => {
        const { cause = {} } = e;
        e.cause = {
          ...cause,
          canvasDrawer,
        };
        onError && onError(e);
        return Promise.reject(e);
      })
      .then(createImageBitmap)
      .then((img) => {
        canvasDrawer.addAnimation(
          new CancelablePromise((resolve) => {
            requestAnimationFrame(() => {
              canvasDrawer.next({ ...canvasStatus, bgColor });
              const { canvas } = canvasDrawer;
              const context = getCanvasContext({ canvas });
              CanvasDrawer.resetCanvas(canvasDrawer);
              context.clearRect(0, 0, canvas.width, canvas.height);

              const imgCanvas = fromImageBitmap(img);

              const width = imgCanvas.width * devicePixelRatio;
              const height = imgCanvas.height * devicePixelRatio;

              context.drawImage(
                imgCanvas,
                0,
                0,
                width,
                height,
                0,
                0,
                canvas.width,
                canvas.height
              );
              canvasDrawer.observable.setValue("status", {
                alpha: 1,
                count: 1,
                total: 1,
              });
              resolve(undefined);
            });
          })
        );
      });
  }, [featureApi.dataRequest]);

  return null;
}
